<template>
  <BannerLayout v-if="isBannerVisible" :bg-color="colors.background">
    <template #text>
      <p data-testid="patient-title" :class="`font-semibold text-${colors.text}`">
        {{ text }}
      </p>
    </template>
    <template #actions>
      <VBtn
        data-testid="consultations-overview-button"
        variant="text"
        class="button-large"
        :class="`text-${colors.text}`"
        :to="{ name: routeNames.CONSULTATIONS }"
        :active="false"
      >
        {{ t('Open consultation overview') }}
      </VBtn>

      <VBtn
        v-if="!isInCall"
        data-testid="join-call-button"
        class="button-large font-semibold text-white"
        :color="colors.button"
        :variant="'flat'"
        :loading="joinCallInProgress"
        @click="joinCall"
      >
        {{ t('Join video call') }}
      </VBtn>

      <div v-if="consultationRequests.length > 1" class="flex items-center gap-1 ml-2">
        <VBtn
          data-testid="carousel-previous"
          icon
          variant="plain"
          size="40"
          :color="colors.carousel"
          :ripple="false"
          :disabled="currentIndex === 0"
          @click="currentIndex--"
        >
          <VIcon :icon="'chevron_left'" size="32" />
        </VBtn>
        <span data-testid="carousel-counter" :class="`text-${colors.carousel} font-semibold`">
          {{ currentIndex + 1 }}/{{ consultationRequests.length }}
        </span>
        <VBtn
          data-testid="carousel-next"
          icon
          variant="plain"
          size="40"
          :color="colors.carousel"
          :ripple="false"
          :disabled="currentIndex === consultationRequests.length - 1"
          @click="currentIndex++"
        >
          <VIcon :icon="'chevron_right'" size="32" />
        </VBtn>
      </div>
      <VBtn
        icon="close"
        variant="plain"
        size="x-small"
        :color="colors.text"
        :ripple="false"
        data-testid="btn-close"
        @click="closeBanner"
      />
    </template>
  </BannerLayout>
</template>

<script setup lang="ts">
import {
  type ConsultationRequestDTO,
  type ScheduledConsultationDTO,
  UrgencyDTO,
  ScheduledConsultationTypeDTO
} from '@/generated'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useCallStore } from '@/stores/call'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import BannerLayout from '@/atoms/BannerLayout.vue'
import { routeNames } from '@/router/routeNames'
import { isScheduledConsultation } from '@/util/isScheduledConsultation'
import { useFormatDate } from '@/composables/useFormatDate'
import { translateScheduledConsultationTypeDTO } from '@/util/translations'

interface Props {
  consultationRequests: (ConsultationRequestDTO | ScheduledConsultationDTO)[]
}

const props = defineProps<Props>()

const { t } = useI18n()

const { isInCall } = storeToRefs(useCallStore())
const { getFormattedPatientName } = useFormattedPatientDetails()
const { formatConferenceDate } = useFormatDate()

const isBannerVisible = ref(true)
const currentIndex = ref(0)

watch(
  () => props.consultationRequests.map((c) => c.id),
  (newIds, oldIds = []) => {
    const hasNewConsultation = newIds.some((id) => !oldIds.includes(id))
    if (hasNewConsultation) {
      isBannerVisible.value = true
    }

    if (currentIndex.value >= newIds.length) {
      currentIndex.value = Math.max(0, newIds.length - 1)
    }
  }
)

const currentConsultation = computed(() => props.consultationRequests[currentIndex.value])

const joinCallInProgress = ref<boolean>(false)

const router = useRouter()

async function joinCall(): Promise<void> {
  const consultation = currentConsultation.value
  const id = consultation?.id
  if (!id) return

  joinCallInProgress.value = true

  if (isScheduledConsultation(consultation)) {
    router.push({ name: routeNames.SCHEDULED_CALL, params: { consultationId: id } })
  } else {
    await useCallStore().joinCall(id)
  }

  joinCallInProgress.value = false
}

const text = computed(() => {
  const consultation = currentConsultation.value

  if (isScheduledConsultation(consultation)) {
    const formattedStartTime = formatConferenceDate(consultation.scheduledStartTime)

    return t('The discussion for the {title} scheduled for {startTime} has started.', {
      title: `${translateScheduledConsultationTypeDTO(consultation.type, t)} ${consultation.type === ScheduledConsultationTypeDTO.CONFERENCE ? `"${consultation.conferenceTopic}"` : ''}`,
      startTime: formattedStartTime
    })
  }

  return t('Consultation discussion regarding patient: {patient} has been started', {
    patient: getFormattedPatientName(consultation.case.patient)
  })
})

const colors = computed(() => {
  const consultation = currentConsultation.value

  if (isScheduledConsultation(consultation)) {
    return { background: 'gray-500', text: 'white', button: 'gray-700', carousel: 'white' }
  }

  switch (consultation.urgency) {
    case UrgencyDTO.EMERGENCY:
      return {
        background: 'error-50',
        text: 'error-700',
        button: 'error-700',
        carousel: 'gray-900'
      }
    case UrgencyDTO.URGENT:
      return {
        background: 'warning-50',
        text: 'warning-700',
        button: 'warning-700',
        carousel: 'gray-900'
      }
    case UrgencyDTO.ROUTINE:
    default:
      return {
        background: 'brand-50',
        text: 'brand-700',
        button: 'brand-700',
        carousel: 'gray-900'
      }
  }
})

function closeBanner() {
  isBannerVisible.value = false
}
</script>

<style scoped></style>
